import * as React from "react";
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber,
  type Country,
} from "react-phone-number-input/input";
import flags from "react-phone-number-input/flags";
import { AsYouType } from "libphonenumber-js";
import { cn } from "@/lib/utils";

interface PhoneInputProps {
  value?: string;
  onChange: (value?: string) => void;
  onBlur?: () => void;
  id?: string;
  name?: string;
  placeholder?: string;
  className?: string;
  "aria-invalid"?: boolean | "true" | "false";
}

const DEFAULT_COUNTRY: Country = "US";
const PREFERRED_COUNTRY_ORDER: Country[] = ["US", "CA", "GB", "AU", "FR", "DE", "ES", "IT"];
const countryFlags = flags as Partial<Record<Country, React.ComponentType<React.SVGProps<SVGSVGElement>>>>;

const getNationalDigits = (phoneValue: string | undefined, country: Country) => {
  if (!phoneValue) return "";

  const parsedPhoneNumber = parsePhoneNumber(phoneValue);
  if (parsedPhoneNumber?.nationalNumber) {
    return parsedPhoneNumber.nationalNumber;
  }

  const digits = phoneValue.replace(/\D/g, "");
  const callingCode = getCountryCallingCode(country);
  return digits.startsWith(callingCode) ? digits.slice(callingCode.length) : digits;
};

const inferCountryFromInternationalValue = (phoneValue: string, currentCountry: Country): Country | undefined => {
  const digits = phoneValue.replace(/\D/g, "");
  if (!digits) return undefined;

  const matchingCountries = getCountries().filter((country) => digits.startsWith(getCountryCallingCode(country)));
  if (!matchingCountries.length) return undefined;

  const longestCallingCodeLength = Math.max(
    ...matchingCountries.map((country) => getCountryCallingCode(country).length),
  );
  const bestMatches = matchingCountries.filter(
    (country) => getCountryCallingCode(country).length === longestCallingCodeLength,
  );

  if (bestMatches.includes(currentCountry)) return currentCountry;
  return PREFERRED_COUNTRY_ORDER.find((country) => bestMatches.includes(country)) || bestMatches[0];
};

const formatDisplayValue = (phoneValue: string, country: Country) => {
  if (!phoneValue) return "";

  const formatter = new AsYouType(phoneValue.trim().startsWith("+") ? undefined : country);
  return formatter.input(phoneValue) || phoneValue;
};

/**
 * Phone input with country flag/code selector.
 * Styled to match the shadcn Input component. Emits E.164 format (e.g. +15555555555).
 */
export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ value, onChange, onBlur, id, name, placeholder, className, ...rest }, ref) => {
    const [selectedCountry, setSelectedCountry] = React.useState<Country>(DEFAULT_COUNTRY);
    const [displayValue, setDisplayValue] = React.useState(() => formatDisplayValue(value || "", DEFAULT_COUNTRY));
    const [isFocused, setIsFocused] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const selectedCountryRef = React.useRef<Country>(DEFAULT_COUNTRY);

    const setInputRef = React.useCallback(
      (node: HTMLInputElement | null) => {
        inputRef.current = node;

        if (typeof ref === "function") {
          ref(node);
          return;
        }

        if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );

    const countryOptions = React.useMemo(() => {
      const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

      return getCountries()
        .map((country) => ({
          country,
          label: regionNames.of(country) || country,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }, []);

    const selectedCountryLabel =
      countryOptions.find((option) => option.country === selectedCountry)?.label || selectedCountry;
    const SelectedFlag = countryFlags[selectedCountry];

    React.useEffect(() => {
      selectedCountryRef.current = selectedCountry;
    }, [selectedCountry]);

    React.useEffect(() => {
      if (inputRef.current && document.activeElement === inputRef.current) {
        return;
      }

      if (isFocused) {
        return;
      }

      if (!value) {
        setDisplayValue("");
        return;
      }

      const parsedPhoneNumber = parsePhoneNumber(value);
      if (parsedPhoneNumber?.country) {
        setSelectedCountry(parsedPhoneNumber.country);
        setDisplayValue(formatDisplayValue(value, parsedPhoneNumber.country));
        return;
      }

      const inferredCountry = value.startsWith("+")
        ? inferCountryFromInternationalValue(value, selectedCountry)
        : undefined;
      if (inferredCountry) {
        setSelectedCountry(inferredCountry);
      }
      setDisplayValue(formatDisplayValue(value, inferredCountry || selectedCountry));
    }, [isFocused, selectedCountry, value]);

    const handleCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
      const nextCountry = event.target.value as Country;
      const nextCallingCode = getCountryCallingCode(nextCountry);
      const nationalDigits = getNationalDigits(value, selectedCountry);
      const nextValue = nationalDigits ? `+${nextCallingCode}${nationalDigits}` : `+${nextCallingCode}`;

      setSelectedCountry(nextCountry);
      setDisplayValue(formatDisplayValue(nextValue, nextCountry));
      onChange(nextValue);
    };

    const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsFocused(true);

      const inputType = (event.nativeEvent as InputEvent | undefined)?.inputType;
      const isDeletion =
        inputType === "deleteContentBackward" ||
        inputType === "deleteContentForward" ||
        event.target.value.length < displayValue.length;

      let rawValue = event.target.value;
      const currentCountry = selectedCountryRef.current;

      if (isDeletion) {
        const hadTrailingPunctuation = /\D$/.test(displayValue.trim());
        const leadingPlus = rawValue.trim().startsWith("+") ? "+" : "";
        let digits = rawValue.replace(/\D/g, "");

        // Deleting a formatting character (e.g. ")") must remove a digit,
        // otherwise the formatter immediately re-inserts it.
        if (hadTrailingPunctuation && digits === displayValue.replace(/\D/g, "")) {
          digits = digits.slice(0, -1);
        }

        rawValue = digits ? `${leadingPlus}${digits}` : leadingPlus;
      }

      const isInternational = rawValue.trim().startsWith("+");
      const formatter = new AsYouType(isInternational ? undefined : currentCountry);
      let formattedValue = formatter.input(rawValue);
      const nextValue = formatter.getNumberValue() || (rawValue ? rawValue : undefined);

      if (isDeletion) {
        // Never show trailing punctuation after a delete, or backspace stalls.
        formattedValue = formattedValue.replace(/[^\d+]+$/, "");
      }

      setDisplayValue(formattedValue || (isDeletion ? rawValue.replace(/[^\d+]+$/, "") : rawValue));
      onChange(nextValue);

      if (isInternational) {
        const parsedPhoneNumber = nextValue ? parsePhoneNumber(nextValue) : undefined;
        if (parsedPhoneNumber?.country) {
          setSelectedCountry(parsedPhoneNumber.country);
          return;
        }

        const inferredCountry = inferCountryFromInternationalValue(rawValue, currentCountry);
        if (inferredCountry) {
          setSelectedCountry(inferredCountry);
        }
      }
    };


    const handleBlur = () => {
      setIsFocused(false);
      onBlur?.();
    };

    return (
      <div
        className={cn(
          "phone-input flex h-10 w-full items-center rounded-md border border-input bg-background ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          className,
        )}
      >
        <div className="phone-input-country relative flex h-full shrink-0 items-center gap-2 pl-3 pr-2">
          <select
            aria-label="Phone number country"
            className="phone-input-country-select absolute inset-0 h-full w-full cursor-pointer opacity-0"
            value={selectedCountry}
            onChange={handleCountryChange}
          >
            {countryOptions.map((option) => (
              <option key={option.country} value={option.country}>
                {option.label} +{getCountryCallingCode(option.country)}
              </option>
            ))}
          </select>
          <span className="phone-input-country-flag" aria-hidden="true">
            {SelectedFlag ? <SelectedFlag className="h-full w-full" /> : null}
          </span>
          <span className="phone-input-country-arrow" aria-hidden="true" />
          <span className="sr-only">{selectedCountryLabel}</span>
        </div>
        <input
          type="tel"
          autoComplete="tel"
          value={displayValue}
          onChange={handlePhoneChange}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          id={id}
          name={name}
          placeholder={placeholder}
          ref={setInputRef}
          aria-invalid={rest["aria-invalid"]}
          className="phone-input-number flex h-10 min-w-0 flex-1 bg-transparent px-3 py-2 text-body-sm placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>
    );
  },
);
PhoneInput.displayName = "PhoneInput";
